import type { GameState } from './types.ts';

export type ConstellationRouteId = 'forge' | 'knowledge' | 'time' | 'prosperity' | 'convergence';
export type ConstellationNodeId =
  | 'livingArchitecture'
  | 'unwrittenLesson'
  | 'echoWorkforce'
  | 'fatefulResonance'
  | 'masterworkConvergence'
  | 'timelessIndustry'
  | 'grandConvergence';

export interface ConstellationNodeDefinition {
  id: ConstellationNodeId;
  route: ConstellationRouteId;
  icon: string;
  name: string;
  description: string;
  cost: number;
  requiredAscensions: number;
  requiredAchievement?: string;
  requiredNodes?: readonly ConstellationNodeId[];
  requiredUpgrade?: { id: string; rank: number };
}

export const CONSTELLATION_NODES: readonly ConstellationNodeDefinition[] = [
  { id: 'livingArchitecture', route: 'forge', icon: '🏛️', name: 'Living Architecture', description: '-5% Hall material costs.', cost: 15, requiredAscensions: 5, requiredAchievement: 'architect', requiredUpgrade: { id: 'harmonicBond', rank: 2 } },
  { id: 'unwrittenLesson', route: 'knowledge', icon: '📖', name: 'The Unwritten Lesson', description: '+10% XP while training skills at level 120 or higher.', cost: 20, requiredAscensions: 8, requiredAchievement: 'veil_student', requiredUpgrade: { id: 'beyondTheVeil', rank: 2 } },
  { id: 'echoWorkforce', route: 'time', icon: '👥', name: 'Echo Workforce', description: '+20% resource production from both Twin Echo skills.', cost: 25, requiredAscensions: 10, requiredAchievement: 'night_shift', requiredUpgrade: { id: 'twinEchoes', rank: 1 } },
  { id: 'fatefulResonance', route: 'prosperity', icon: '⚡', name: 'Fateful Resonance', description: 'Critical chance rises from 2% to 2.25%; Perfect Strike gold gains +25%.', cost: 15, requiredAscensions: 6, requiredAchievement: 'stroke_of_fate', requiredUpgrade: { id: 'swiftHands', rank: 3 } },
  { id: 'masterworkConvergence', route: 'convergence', icon: '☄️', name: 'Masterwork Convergence', description: '+3% global XP and resource production.', cost: 40, requiredAscensions: 15, requiredAchievement: 'starforged', requiredNodes: ['livingArchitecture', 'unwrittenLesson'] },
  { id: 'timelessIndustry', route: 'convergence', icon: '⌛', name: 'Timeless Industry', description: 'Production Surge rewards last 25% longer when activated.', cost: 40, requiredAscensions: 15, requiredAchievement: 'unwavering', requiredNodes: ['echoWorkforce', 'fatefulResonance'] },
  { id: 'grandConvergence', route: 'convergence', icon: '🌠', name: 'Grand Convergence', description: '+5% global XP and resource production.', cost: 100, requiredAscensions: 26, requiredAchievement: 'beyond_all_veils', requiredNodes: ['masterworkConvergence', 'timelessIndustry'] }
];

export function constellationNode(id: ConstellationNodeId): ConstellationNodeDefinition {
  const node = CONSTELLATION_NODES.find((candidate) => candidate.id === id);
  if (!node) throw new Error(`Unknown Constellation node: ${id}`);
  return node;
}

export function ownsConstellationNode(state: GameState, id: ConstellationNodeId): boolean {
  return state.prestige.constellationNodes?.includes(id) ?? false;
}

export function constellationNodeLockReasons(state: GameState, id: ConstellationNodeId): string[] {
  const node = constellationNode(id);
  const reasons: string[] = [];
  if (state.prestige.ascensions < node.requiredAscensions) reasons.push(`A${node.requiredAscensions}`);
  if (node.requiredAchievement && !(state.prestige.achievements ?? []).includes(node.requiredAchievement)) reasons.push('Achievement');
  if (node.requiredUpgrade && (state.prestige.upgrades[node.requiredUpgrade.id as keyof typeof state.prestige.upgrades] ?? 0) < node.requiredUpgrade.rank) {
    reasons.push(`${node.requiredUpgrade.id} ${node.requiredUpgrade.rank}`);
  }
  for (const required of node.requiredNodes ?? []) {
    if (!ownsConstellationNode(state, required)) reasons.push(constellationNode(required).name);
  }
  return reasons;
}

export function constellationGlobalMultiplier(state: GameState): number {
  return (ownsConstellationNode(state, 'masterworkConvergence') ? 1.03 : 1)
    * (ownsConstellationNode(state, 'grandConvergence') ? 1.05 : 1);
}

export function constellationHallCostMultiplier(state: GameState): number {
  return ownsConstellationNode(state, 'livingArchitecture') ? 0.95 : 1;
}

export function constellationDeepXpMultiplier(state: GameState): number {
  return ownsConstellationNode(state, 'unwrittenLesson') ? 1.1 : 1;
}

export function constellationTwinEchoMultiplier(state: GameState): number {
  return ownsConstellationNode(state, 'echoWorkforce') ? 1.2 : 1;
}

export function constellationCriticalChance(state: GameState): number {
  return ownsConstellationNode(state, 'fatefulResonance') ? 0.0225 : 0.02;
}

export function constellationPerfectStrikeMultiplier(state: GameState): number {
  return ownsConstellationNode(state, 'fatefulResonance') ? 1.25 : 1;
}

export function constellationSurgeDurationMultiplier(state: GameState): number {
  return ownsConstellationNode(state, 'timelessIndustry') ? 1.25 : 1;
}
